import React, { useState, useEffect, createRef } from 'react';
import cx from 'classnames';
import { toast } from 'react-hot-toast';

import { useMediaApi } from '@api/media';
import { fileToBase64 } from '@helpers/files';
import { confirmMessage } from '@helpers/messages';
import { BasePreloader, Button } from '@components';

import { useNewsApi } from '@api/news';
import { applicationStore } from '@stores';
import { AdminAuctionImageType } from '@types';
import { getFormatBytes } from '@helpers/formats';
import MediaModal from '../../AuctionsUpdatePage/components/MediaModal';

import plusIco from '@assets/images/plus-ico.svg';
import colorIcoDel from '@assets/images/color-ico-del.svg';
import plusIcoWhite from '@assets/images/plus-ico-white.svg';

interface NewImageProps {
  image: ImageType;
  onClick: () => void;

  onDelete(event: React.MouseEvent, image: ImageType): void;

  onDragStart(imageId: string | number, event: React.DragEvent): void;

  onDragEnd(imageId: string | number): void;

  onDragOver(imageId: string | number): void;
}

const NewImage: React.FC<NewImageProps> = ({ image, onDelete, onDragStart, onDragEnd, onDragOver, onClick }) => {
  return (
    <div
      className={cx('item-image isDraggable')}
      onDragStart={(event: React.DragEvent) => onDragStart(image.id, event)}
      onDragEnd={() => onDragEnd(image.id)}
      onDragOver={() => onDragOver(image.id)}
      draggable
    >
      {image.type !== 'file' && !image.type.startsWith('image') && (
        <div className="video-icon" onClick={onClick}>
          <svg
            role="img"
            focusable="false"
            data-prefix="far"
            aria-hidden="true"
            viewBox="0 0 512 512"
            data-icon="play-circle"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#ddd"
              d="M371.7 238l-176-107c-15.8-8.8-35.7 2.5-35.7 21v208c0 18.4 19.8 29.8 35.7 21l176-101c16.4-9.1 16.4-32.8 0-42zM504 256C504 119 393 8 256 8S8 119 8 256s111 248 248 248 248-111 248-248zm-448 0c0-110.5 89.5-200 200-200s200 89.5 200 200-89.5 200-200 200S56 366.5 56 256z"
            ></path>
          </svg>
        </div>
      )}
      <div
        className="image-content"
        style={{ backgroundImage: `url(${image.src})`, backgroundSize: 'cover' }}
        onClick={onClick}
      />
      <div className="image-action text-center pt-1">
        <a href="/" onClick={(e) => onDelete(e, image)} className="d-inline-flex align-items-center">
          <img src={colorIcoDel} alt="ico" className="mr-2" />
          Smazat
        </a>
      </div>
    </div>
  );
};

interface DraggableNewImagesProps {
  images: Array<ImageType>;

  onDelete(event: React.MouseEvent, image: ImageType): void;

  onReorder(order: Array<string | number>): void;
}

const DraggableNewImages: React.FC<DraggableNewImagesProps> = ({ images, onDelete, onReorder }) => {
  const [order, setOrder] = React.useState<Array<string | number>>(images.map((image) => image.id));
  const [draggedImageId, setDraggedImageId] = React.useState<string | number | null>(null);
  const [imageSelected, setImageSelected] = React.useState<AdminAuctionImageType | undefined>(undefined);

  const handleDragStart = (imageId: string | number, event: React.DragEvent) => {
    setDraggedImageId(imageId);
  };

  const handleDragEnd = (imageId: string | number) => {
    setDraggedImageId(null);
    onReorder(order);
  };

  const handleDragOver = (imageId: string | number) => {
    if (!draggedImageId || imageId === draggedImageId) {
      return null;
    }

    const newOrder = [...order];

    newOrder.splice(newOrder.indexOf(draggedImageId), 1);
    newOrder.splice(newOrder.indexOf(imageId), 0, draggedImageId);

    setOrder(newOrder);
  };

  if (images.length < 1) {
    return <div className="no-items mt-2">Nebyla nalezena žádná data.</div>;
  }

  return (
    <>
      {!!imageSelected && <MediaModal item={imageSelected} onRequestClose={() => setImageSelected(undefined)} />}
      <div className="images">
        {images
          .sort((imageA, imageB) => order.indexOf(imageA.id) - order.indexOf(imageB.id))
          .map((image, index) => (
            <NewImage
              key={index}
              image={image}
              onDelete={onDelete}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onClick={() => setImageSelected({ type: image.type, src: image.src, embedHash: image.embedHash })}
            />
          ))}
      </div>
    </>
  );
};

interface Props {
  id: string | number;
}

interface ImageType {
  id: string | number;
  src: string;
  type: string;
  embedHash?: string;
}

const NewImages: React.FC<Props> = (props) => {
  const mediaApi = useMediaApi();
  const newsApi = useNewsApi();
  const [loaded, setLoaded] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [saved, setSaved] = useState(true);
  const [images, setImages] = useState<ImageType[]>([]);
  const inputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    loadImages();
    return () => newsApi.cancelAllRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadImages = async () => {
    try {
      const response = await newsApi.newsMediaList(props.id);
      const images: ImageType[] = [];
      const urlCreator = window.URL || window.webkitURL;
      for (let i = 0; i < response.data.length; i++) {
        try {
          const media = await mediaApi.detail(response.data[i].media.hash, true);
          images.push({
            id: response.data[i].id,
            type: response.data[i].media.type !== 'file' ? response.data[i].media.mimeType : 'file',
            src: urlCreator.createObjectURL(media.data),
            embedHash: response.data[i].media.embedHash,
          });
        } catch (err) {
          images.push({
            src: '',
            id: response.data[i].id,
            type: response.data[i].media.type !== 'file' ? response.data[i].media.mimeType : 'file',
            embedHash: response.data[i].media.embedHash,
          });
        }
      }
      setImages(images);
      setLoaded(true);
      setSaved(true);
    } catch (err) {
      if (!err.response) {
        return;
      }
      setLoaded(true);
    }
  };

  const handleSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target?.files) {
      return;
    }
    setSaved(false);
    for (let i = 0; i < e.target.files.length; i++) {
      await saveFile(e.target.files[i]);
    }
    setSaved(true);
    setLoaded(false);
    loadImages();
  };

  const handleDeleteClick = (e: React.MouseEvent, item: ImageType) => {
    e.preventDefault();
    confirmMessage({
      title: 'Potvrzení',
      text: 'Opravdu si přejete odebrat tuto položku?',
      showLoaderOnConfirm: true,
      preConfirm: () => {
        return new Promise(async (resolve) => {
          try {
            await newsApi.deleteNewsImage(props.id, item.id);
            await loadImages();
            resolve(true);
          } catch {
            resolve(true);
          }
        });
      },
    });
  };

  const saveFile = async (file: File) => {
    const maxPostBodySize = applicationStore.getState().systemInfo?.post_max_size;
    if (!!file && !!maxPostBodySize && maxPostBodySize < file.size) {
      toast.error(`Soubor ${file.name} nesmí být větší než ${getFormatBytes(maxPostBodySize)}`);
      return;
    }

    try {
      const fileBase64 = await fileToBase64(file as File);
      await newsApi.createNewsImage(props.id, {
        type: 'image',
        data: fileBase64,
        mime: file?.type as string,
        image_title: file.name,
        original_name: file.name,
      });
    } catch (err) {
      if (!err.response) {
        return;
      }
    }
  };

  const handleReorder = async (order: Array<string | number>) => {
    setIsSavingOrder(true);

    try {
      await newsApi.reorderNewsImages(
        props.id,
        order.map((id, index) => ({
          newsMediaId: id,
          position: index,
        }))
      );
    } catch (err) {
      if (!err.response) {
        return;
      }
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <>
      <div className="auction-images">
        {' '}
        <div className="item-button d-flex align-items-center">
          <Button type="button" className="mr-2" onClick={() => inputRef.current?.click()}>
            <img src={plusIco} alt="ico" className="mr-2 hover-hide" />
            <img src={plusIcoWhite} alt="ico" className="mr-2 hover-show" />
            Přidat obrázky
          </Button>
          {!saved && <BasePreloader className="image-preloader" size={25} />}
          {saved && (
            <input
              multiple
              type="file"
              ref={inputRef}
              className="d-none"
              accept="image/jpeg,image/gif,image/bmp,image/png,image/webp"
              onChange={handleSelectFile}
            />
          )}
        </div>
        <div className="item-help mt-2">* První obrázek galerie je hlavním obrázkem.</div>
        {loaded ? (
          <div className="items-list">
            {isSavingOrder ? (
              <BasePreloader size={25} />
            ) : (
              <DraggableNewImages images={images} onDelete={handleDeleteClick} onReorder={handleReorder} />
            )}
          </div>
        ) : (
          <div className="mt-4 mb-4">
            <BasePreloader size={25} />
          </div>
        )}
      </div>
    </>
  );
};

export default NewImages;
